/* PolicyDetailsSubviewRenderer.js */

"use strict";
const Forms = require("@gluenetworks/forms");
const FormsComponent = Forms.ui.component;
const _ = require("lodash");
const Registrations = require("../../../../../../../RegistrationNamespaces");

class PolicyDetailsSubviewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("policy-details-subview-view-gl");
        this._rootElem.component = this._model;
        this._logger = this.model.logger;

        this._formsComponent = new FormsComponent();
        this._registerListeners();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        this._unregisterListeners();

        let promArr = [this._formsComponent.shutdown()];

        return Promise.all(promArr).then(
            () => {
                this._formsComponent = undefined;
                this._renderingForm = undefined;
                this._logger = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this._logger.warn({ error: err }, "PolicyDetailsSubviewRenderer.shutdown() error.");
            }
        );
    }

    _addViewActions() {
        this._model._gameEditor.addViewAction({
            type: "normal",
            displayName: "Back",
            name: "back",
            handler: this._onBackButtonClick.bind(this)
        });
    }

    _registerListeners() {}

    _unregisterListeners() {}

    _renderPolicyDetails() {
        return Promise.resolve().then(
            () => {
                let jtype = this._getJtype();
                let pristineValue = this._prepareValues();
                this._formsComponent.setTypeInformation(jtype, true);
                this._formsComponent.setPristineValue(pristineValue, true);
                this._addViewActions();
                return this._formsComponent.renderer.render().then(() => {
                    return this._renderConceptItemCommands();
                });
            },
            (err) => {
                this._logger.warn({ error: err }, "PolicyDetailsSubviewRenderer._renderPolicyDetails() error.");
            }
        );
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (!this._rootElem.hasChildNodes()) {
            this._rootElem.appendChild(this._formsComponent.renderer.rootElement);
        }
        return this.model.whenModelIsReady().then(
            () => {
                return this._renderPolicyDetails();
            },
            (err) => err
        );
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    _onBackButtonClick() {
        this._model._gameEditor.setActiveView(Registrations.AUDIT_POLICY_EDITOR_VIEW_HISTORY, [
            {
                value: {
                    fqn: Registrations.AUDIT_POLICY_EDITOR_SUBVIEW_DETAILS_VIEW
                }
            }
        ]);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    _renderConceptItemCommands() {
        let conceptDropdown = document.querySelectorAll(".lm_item_container.focused_item .concept_item_id > gl-multi-select"),
            scopeInputs = document.querySelectorAll(".lm_item_container.focused_item .scope > input-text-control > input");
        const form = !_.isEmpty(this._formsComponent) ? this._formsComponent.getValue() : {};
        if (form.rules) {
            //remove all old command additional items before add new
            this._removeOldCommandValues();
            form.rules.forEach((rule, idx) => {
                if (rule.conceptItemId) {
                    const conceptItem = this.model.gameEditor.conceptItems[rule.conceptItemId];
                    if (conceptItem && conceptItem.showCommands) {
                        const commands = conceptItem.showCommands;
                        commands.forEach((command) => {
                            let commandContainer = document.createElement("div");
                            commandContainer.classList.add("additional-command-container");
                            commandContainer.innerText = command;
                            if (!_.isEmpty(conceptDropdown[idx])) {
                                conceptDropdown[idx].appendChild(commandContainer);
                            }
                        });
                    }
                    if (conceptItem && conceptItem.keyCliLine && conceptItem.keyCliLine.length > 0) {
                        scopeInputs[idx].setAttribute("placeholder", `Regular Expression (${conceptItem.keyCliLine})`);
                    }
                }
            });
        }
        return Promise.resolve();
    }

    /**
     * Remove all additional concept items
     */
    _removeOldCommandValues() {
        let oldCommandValues = document.querySelectorAll(".lm_item_container.focused_item .concept_item_id .additional-command-container"),
            scopeInputs = document.querySelectorAll(".lm_item_container.focused_item .scope > input-text-control > input");
        if (oldCommandValues) {
            oldCommandValues.forEach((oldCommandItem) => {
                oldCommandItem.remove();
            });
        }
        scopeInputs.forEach((scopeInput) => {
            scopeInput.setAttribute("placeholder", "Regular Expression");
        });
    }

    _prepareValues() {
        const pristineProperties = this.model.auditPolicy;
        let pristineValues = _.cloneDeep(pristineProperties);

        if (pristineValues.rules) {
            pristineValues.rules.forEach((rule) => {
                if (rule.os === "") {
                    rule.os = "any";
                }
            });
        }

        return pristineValues;
    }

    _getJtype() {
        const osItems = this.model.gameEditor.os,
            conceptItems = this.model.gameEditor.conceptItems;
        let conceptItemsValues = Object.keys(conceptItems),
            defaultConceptItemValues = [],
            defaultConceptItemLabels = [];

        conceptItemsValues.forEach((key) => {
            let item = conceptItems[key];
            defaultConceptItemValues.push(key);
            defaultConceptItemLabels.push(item.label);
        });

        let osSelectOptions = Object.keys(osItems).reduce(
            (result, osItemKey) => {
                let filteredConceptItemsValues = [],
                    filteredConceptItemsLabels = [];
                if (conceptItemsValues) {
                    conceptItemsValues.forEach((itemKey) => {
                        const item = conceptItems[itemKey];
                        if (typeof item.os === "undefined" || item.os.includes(osItems[osItemKey])) {
                            filteredConceptItemsValues.push(item.value);
                            filteredConceptItemsLabels.push(item.label);
                        }
                    });
                }
                result[osItemKey] = {
                    "conceptItemId@Display.label": "Concept",
                    "conceptItemId@Enum": filteredConceptItemsValues,
                    "conceptItemId@Display.enum": filteredConceptItemsLabels
                };
                result[`${osItemKey}@Display.label`] = osItems[osItemKey];
                return result;
            },
            {
                "any@Display.label": "Any",
                any: {
                    "conceptItemId@Display.label": "Concept",
                    "conceptItemId@Enum": defaultConceptItemValues,
                    "conceptItemId@Display.enum": defaultConceptItemLabels
                }
            }
        );

        return {
            "name@Display.label": "Name",
            name: "",
            "name@Display.readOnly": true,

            "description@Display.label": "Description",
            description: "",
            "description@Display.readOnly": true,

            "lineBreak@Display.label": "&nbsp;",

            "rules@Display.label": "Policy Rules",
            "rules@Display.addLabel": "Add New Rule",
            "rules@Display.readOnly": true,
            rules: [
                {
                    "name@Display.label": "Rule Name",
                    name: "",

                    "description@Display.label": "Description",
                    description: "",

                    "filter@Display.label": "Filter",
                    filter: null,

                    "os@Display.label": "OS",
                    "os@Display.placeholder": " ",
                    os: "any",
                    "os@Required!": "any",
                    "os@Display.sort": false,
                    "os@Select.options": osSelectOptions,

                    "scope@Display.label": "Scope",
                    "scope@Display.placeholder": "Regular Expression",
                    scope: "",

                    "scopeSeparateResults@Display.label": "Evaluate each result of the filter individually",
                    "scopeSeparateResults@Display.description":
                        "Option to evaluate the rule against each filtered item independently or as a single item",
                    scopeSeparateResults: false,

                    "expected@Display.label": "Required",
                    expected: [],
                    "expected@Display.multiline": true,
                    "expected@Display.text": { minLineCount: 5 },
                    "expected@Display.placeholder": "Regular Expression",
                    "expected@Display.editor": {},

                    "forbidden@Display.label": "Forbidden",
                    forbidden: [],
                    "forbidden@Display.multiline": true,
                    "forbidden@Display.text": { minLineCount: 5 },
                    "forbidden@Display.placeholder": "Regular Expression",
                    "forbidden@Display.editor": {},

                    "expectedAll@Display.label": "All",
                    expectedAll: false,

                    "expectedOrdered@Display.label": "Ordered",
                    expectedOrdered: false,

                    "expectedOnly@Display.label": "Only",
                    expectedOnly: false,

                    "forbiddenAll@Display.label": "All",
                    forbiddenAll: false
                }
            ]
        };
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = PolicyDetailsSubviewRenderer;
